import { CallLogsTable } from '@/components/call-logs-table/call-logs-table';
import { useCallLog } from '@/hooks/use-call-log/use-call-log';
import { useExportCallLogs } from '@/hooks/use-export-call-logs/use-export-call-logs';
import { Outlet, useParams } from 'react-router';
import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import {
  DEFAULT_PAGINATION,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { Call, CallLogFilters } from '@/services/call-log-service/call-log-service.types';
import { Flex, message } from 'antd';
import { Suspense, lazy, useEffect, useState } from 'react';
import { DEFAULT_TIME_RANGE } from './conversations.constans';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { ExportHistoryDrawer } from '@/components/export-history-drawer/export-history-drawer';
import { useExportHistory } from '@/hooks/use-export-history/use-export-history';
import { useExportHistoryFile } from '@/hooks/use-export-history-file/use-export-history-file';
import styles from './conversations.module.scss';
import { useCompanyStore } from '@/store/app.store';
import { useUser } from '@/hooks/use-user/use-user';
import { ConversationsHeader } from '@/components/conversations-header/conversations-header';
import { Dayjs } from 'dayjs';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { getRecordingUrl } from '@/services/conversations-service/conversations-service';

const FloatingAudioPlayer = lazy(
  () => import('@/components/floating-audio-player/floating-audio-player')
);

export function Conversations() {
  const [selectedRecord, setSelectedRecord] = useState<Call | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingRecordId, setLoadingRecordId] = useState<number | undefined>(undefined);
  const [isExportHistoryDrawerVisible, setExportHistoryDrawerVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { companyName, setCompanyName } = useCompanyStore();
  const { companies } = useCompaniesList();

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;

  useEffect(() => {
    if (companyName) {
      setCallLogsFilters((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setCallLogsFilters((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const {
    callLogExportHistoryData,
    refetch: refetchExportHistory,
    isPending: isLoadingData,
  } = useExportHistory({
    page: currentPage,
    items_per_page: perPage,
  });
  const [messageApi, contextHolder] = message.useMessage();

  const { callId } = useParams();
  const items = useBreadcrumbs();

  const [callLogsFilters, setCallLogsFilters] = useState<CallLogFilters>({
    time_range: DEFAULT_TIME_RANGE,
    duration_min: undefined,
    duration_max: undefined,
    query: undefined,
    status: undefined,
    agent: undefined,
    export: undefined,
    company_name: companyName || (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
    start_time: undefined,
    end_time: undefined,
  });

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultSorting: { sort_by: 'created_at', order_by: 'desc' },
    });

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    onPaginationChanged(
      page || DEFAULT_PAGINATION.page,
      perPage || DEFAULT_PAGINATION.items_per_page
    );
    onSortChange(sortBy || 'created_at', orderBy || 'desc');
  };

  const handleSelectRecord = async (record: Call) => {
    setLoadingRecordId(record.id);
    try {
      const response = await getRecordingUrl(record.id);
      setSelectedRecord({ ...record, recordingUrl: response.url } as Call & { recordingUrl: string });
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: 'Failed to load recording',
      });
    } finally {
      setLoadingRecordId(undefined);
    }
  };

  const {
    callLogData,
    refetch: refetchCallLog,
    trendingTopics,
    isPendingTrendingTopics,
    isPending: isLoadingCallLog,
  } = useCallLog({
    filtersParams: {
      ...callLogsFilters,
      company_name: callLogsFilters.company_name,
      time_zone: timeZone,
    },
    paginationParams: { ...paginationDetails, ...sortDetails },
  });

  const { exportCallLogs } = useExportCallLogs({
    filtersParams: {
      ...callLogsFilters,
      company_name: callLogsFilters.company_name,
    },
    paginationParams: { order_by: 'desc', sort_by: 'created_at', page: 1, items_per_page: 10 },
  });

  const { prepareCSV, isPending, exportContextHolder } = useExportHistoryFile();

  const handleRefreshData = () => {
    refetchCallLog();
  };

  const handlePrepareExportData = async () => {
    setIsExporting(true);
    const response = await exportCallLogs();

    if (response) {
      messageApi.open({
        type: 'info',
        content: 'Processing a file',
      });
    }
    setIsExporting(false);
    refetchExportHistory();
  };

  const handleExportData = (id: number) => {
    prepareCSV(id);
  };

  const handleExportHistory = () => {
    setExportHistoryDrawerVisible(true);
  };

  const closeExportHistoryDrawer = () => {
    setExportHistoryDrawerVisible(false);
  };

  const handleCompanyChange = (filters: CallLogFilters) => {
    setCallLogsFilters(filters);
    setCompanyName(filters.company_name ?? undefined);
  };

  const onClearDateRangeFilter = () => {
    setCallLogsFilters((prevState) => ({
      ...prevState,
      time_range: '7d',
      start_time: undefined,
      end_time: undefined,
    }));

    setDates(null);
  };

  const onChangeDateRangeFilter = (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => {
    setCallLogsFilters((prevState) => ({
      ...prevState,
      time_range: undefined,
      start_time: filterValue.time_range.start_time,
      end_time: filterValue.time_range.end_time,
      topic: undefined,
    }));
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />
      {callId ? (
        <Outlet />
      ) : (
        <div>
          <div>
            {contextHolder && <div key="message-holder">{contextHolder}</div>}
            {exportContextHolder && <div key="export-message-holder">{exportContextHolder}</div>}
          </div>

          <ConversationsHeader
            onSearchQueryChange={setCompanySearchQuery}
            searchResults={companySearchData}
            isSearchLoading={companySearchLoading}
            searchError={companySearchError}
            calls={
              callLogData?.calls_count_relation === 'gte'
                ? `${callLogData.calls_count.toLocaleString()}+`
                : callLogData?.calls_count || 0
            }
            trendingTopics={trendingTopics?.slice(0, 5) || []}
            onRefreshData={handleRefreshData}
            onExportHistory={handleExportHistory}
            filters={callLogsFilters}
            onChangeFilters={handleCompanyChange}
            companiesList={companies?.map((company) => company.name) || []}
            dates={dates}
            setDates={setDates}
            onDateRangeClear={onClearDateRangeFilter}
            onChangeDateRangeFilter={onChangeDateRangeFilter}
            isTrendingTopicsLoading={isPendingTrendingTopics}
            isCompanySelectDisabled={!!isCompanyAdmin && !hasMultipleCompanies}
          />

          <ExportHistoryDrawer
            isEmptyData={!(callLogData && callLogData?.calls.length > 0)}
            isExporting={isExporting}
            onPrepareExport={handlePrepareExportData}
            isDownloading={isPending}
            onExportClick={handleExportData}
            isVisible={isExportHistoryDrawerVisible}
            onClose={closeExportHistoryDrawer}
            exportHistoryData={callLogExportHistoryData?.reports}
            isLoadingData={isLoadingData}
            currentPage={currentPage}
            perPage={perPage}
            totalExports={callLogExportHistoryData?.reports_count || 0}
            onChangePagination={(page, pageSize) => {
              setCurrentPage(page);
              setPerPage(pageSize);
            }}
          />

          <CallLogsTable
            currentPage={paginationDetails.page}
            onChange={handleChange}
            onSelectRecord={handleSelectRecord}
            callLogData={callLogData?.calls}
            pagination={callLogData?.pagination}
            selectedRecord={selectedRecord}
            setIsPlaying={setIsPlaying}
            loading={isLoadingCallLog}
            loadingRecordId={loadingRecordId}
          />

          {selectedRecord && selectedRecord.duration && (
            <Suspense key={selectedRecord.id}>
              <FloatingAudioPlayer
                onPlayerClose={() => setSelectedRecord(undefined)}
                data={selectedRecord}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
              />
            </Suspense>
          )}
        </div>
      )}
    </Flex>
  );
}
